import { action, observable, computed } from 'mobx';

import MessagingService from 'services/MessagingService/MessagingService';
import {
  CLOSE_BROADCASTER_SEARCH,
  SET_BROADCASTER_BANNED_USERS_IN_ROOM,
  SET_BROADCASTER_MODERATION_ERROR_VALUE,
  SET_BROADCASTER_MODERATORS_IN_ROOM,
  SET_BROADCASTER_SILENCED_USERS_IN_ROOM,
} from 'services/MessagingService/EventNames';
import ModerationService from 'services/ModerationService';
import FirebaseStore from 'stores/FirebaseStore';
import ChatStore from 'stores/ChatStore';
import UserStore from 'stores/UserStore';
import { ACCESS_LEVEL_STR } from 'pages/User/AccessLevel';
import { MODERATION_DRAWER_NAMES } from 'enum/helpers';
import { MODERATION_OPTIONS } from 'utils/consts';
import { NOTIFICATION_TYPE } from 'enum/messages';


class ModerationStore {
  constructor(messagingService) {
    this.messagingService = messagingService();
    this.messagingService.on(SET_BROADCASTER_MODERATORS_IN_ROOM, this.setRoomModeratorsList);
    this.messagingService.on(SET_BROADCASTER_SILENCED_USERS_IN_ROOM, this.setRoomSilencedUsers);
    this.messagingService.on(SET_BROADCASTER_BANNED_USERS_IN_ROOM, this.setRoomBannedUsers);
    this.messagingService.on(SET_BROADCASTER_MODERATION_ERROR_VALUE, this.setIsModerationErrorVisible);
    this.messagingService.on(CLOSE_BROADCASTER_SEARCH, this.closeSearch);
  }

  @observable anchorForModerationPopper = null;
  @observable isModerationPopperOpen = false;
  @observable searchValue = '';
  @observable isSearchOpen = false;
  @observable moderatorsList = [];
  @observable silencedList = [];
  @observable bansList = [];
  @observable isModerationErrorVisible = false;
  @observable isUserAlreadyInList = false;
  @observable drawerName = '';

  destructor() {
    this.messagingService.off(SET_BROADCASTER_MODERATORS_IN_ROOM, this.setRoomModeratorsList);
    this.messagingService.off(SET_BROADCASTER_SILENCED_USERS_IN_ROOM, this.setRoomSilencedUsers);
    this.messagingService.off(SET_BROADCASTER_BANNED_USERS_IN_ROOM, this.setRoomBannedUsers);
    this.messagingService.off(SET_BROADCASTER_MODERATION_ERROR_VALUE, this.setIsModerationErrorVisible);
    this.messagingService.off(CLOSE_BROADCASTER_SEARCH, this.closeSearch);
  }

  /**
   * Method for closing moderation popper
   */
  @action closeModerationPopper = () => {
    this.anchorForModerationPopper = null;
    this.isModerationPopperOpen = false;
  };

  /**
   * Method for opening moderation popper
   */
  @action openModerationPopper = (event) => {
    this.anchorForModerationPopper = this.anchorForModerationPopper ? null : event.currentTarget;
    this.isModerationPopperOpen = Boolean(this.anchorForModerationPopper);
  };

  /**
   * Method for opening moderation drawer for silenced, banned, moderator users
   */
  @action openModerationDrawer = (name) => {
    this.closeModerationPopper();
    this.drawerName = name;
  };

  /**
   * Method for closing moderation drawer and reseting errors
   */
  @action closeModerationDrawer = () => {
    this.drawerName = '';
    this.searchValue = '';
    this.isUserAlreadyInList = false;
    this.isModerationErrorVisible = false;
  };

  /**
   * Method for setting search value in moderation drawer
   */
  @action setSearchValue = (value) => {
    this.setIsModerationErrorVisible(false);
    this.isUserAlreadyInList = false;
    this.searchValue = value;
  };

  /**
   * Method for opening custom search dropdown
   */
  @action setIsSearchOpen = (value) => {
    this.isSearchOpen = value;
    this.isUserAlreadyInList = false;
  };

  /**
   * Method for closing custom search dropdown
   */
  @action closeSearch = () => {
    this.searchValue = '';
    this.isSearchOpen = false;
  };

  /**
   * Method for seting moderator list in moderation drawer
   */
  @action setRoomModeratorsList = (list) => {
    this.moderatorsList = list ? Object.values(list).map((item) => {
      return { name: item.ou };
    }) : [];
  };

  /**
   * Method for seting silenced users list in moderation drawer
   */
  @action setRoomSilencedUsers = (list) => {
    this.silencedList = list ? Object.values(list).map((item) => {
      return { name: item.ou };
    }) : [];
  };

  /**
   * Method for seting banned users list in moderation drawer
   */
  @action setRoomBannedUsers = (list) => {
    this.bansList = list ? Object.values(list).map((item) => {
      return { name: item.ou };
    }) : [];
    const bansListUsername = this.bansList.map((ban) => ban.name);
    const filteredMessages = ChatStore.messages.filter((msg) =>
      !bansListUsername.includes(msg.senderOriginalUsername) || msg.type !== NOTIFICATION_TYPE.MESSAGE);
    ChatStore.messages = filteredMessages;
  };

  /**
   * Method for seting error in custom search/dropdown
   */
  @action setIsModerationErrorVisible = (value) => {
    this.isModerationErrorVisible = value;
  };

  /**
   * Method for handling user clicked in search/dropdown
   */
  handleSelectModerationItem = (target) => {
    this.searchValue = target;
    switch (this.drawerName) {
      case MODERATION_DRAWER_NAMES.MODERATORS:
        this.handleSelectModerator(target);
        break;
      case MODERATION_DRAWER_NAMES.SILENCES:
        this.handleSelectSilencedUser(target);
        break;
      case MODERATION_DRAWER_NAMES.BANS:
        this.handleSelectBannedUser(target);
        break;
      default:
        break;
    }
  };

  /**
   * Method for handling user clicked in moderator drawer
   */
  handleSelectModerator = (target) => {
    if (this.moderatorsList.some((item) => item.name.toLocaleLowerCase() === target.toLocaleLowerCase())) {
      this.isUserAlreadyInList = true;
    } else {
      this.moderateUser(target);
    }
  };

  /**
   * Method for handling user clicked in silences drawer
   */
  handleSelectSilencedUser = (target) => {
    if (this.silencedList.some((item) => item.name.toLocaleLowerCase() === target.toLocaleLowerCase())) {
      this.isUserAlreadyInList = true;
    } else {
      this.moderateUser(target);
    }
  };

  /**
   * Method for handling user clicked in bans drawer
   */
  handleSelectBannedUser = (target) => {
    if (this.bansList.some((item) => item.name.toLocaleLowerCase() === target.toLocaleLowerCase())) {
      this.isUserAlreadyInList = true;
    } else {
      this.moderateUser(target);
    }
  };

  /**
   * Method for adding user moderations list
   */
  moderateUser = (recipient) => {
    const data = {
      recipient,
      roomname: UserStore.user.username,
      username: UserStore.user.username,
      accessHash: UserStore.user.accessHash,
    };
    switch (this.drawerName) {
      case MODERATION_DRAWER_NAMES.MODERATORS:
        this.setModerator(data);
        break;
      case MODERATION_DRAWER_NAMES.SILENCES:
        this.silenceUser(data);
        break;
      case MODERATION_DRAWER_NAMES.BANS:
        this.banUser(data);
        break;
      default:
        break;
    }
  };

  /**
   * Method for adding user to be moderator
   */
  setModerator = (data, isContextualMenu) => {
    const postData = {
      ...data,
      type: MODERATION_OPTIONS.MODERATOR,
    };
    ModerationService.moderateUser(postData, isContextualMenu);
  };

  /**
   * Method for adding user in silenced list
   */
  silenceUser = (data, isContextualMenu) => {
    const postData = {
      ...data,
      type: MODERATION_OPTIONS.SILENCE,
    };
    ModerationService.moderateUser(postData, isContextualMenu);
  };

  /**
   * Method for adding user in bans list
   */
  banUser = (data, isContextualMenu) => {
    const postData = {
      ...data,
      type: MODERATION_OPTIONS.BAN,
    };
    ModerationService.moderateUser(postData, isContextualMenu);
  };

  /**
   * Method for kicking user
   */
  kickUser = (data, isContextualMenu) => {
    const postData = {
      ...data,
      type: MODERATION_OPTIONS.KICK,
    };
    ModerationService.moderateUser(postData, isContextualMenu);
  };

  /**
   * Method for removing user from moderators list
   */
  removeModerator = (data, isContextualMenu) => {
    const postData = {
      ...data,
      type: MODERATION_OPTIONS.REMOVE_MODERATOR,
    };
    ModerationService.moderateUser(postData, isContextualMenu);
  };

  /**
   * Method for removing user from silenced list
   */
  removeSilence = (data, isContextualMenu) => {
    const postData = {
      ...data,
      type: MODERATION_OPTIONS.UNSILENCE,
    };
    ModerationService.moderateUser(postData, isContextualMenu);
  };

  /**
   * Method for removing user from bans list
   */
  removeBan = (data) => {
    const postData = {
      ...data,
      type: MODERATION_OPTIONS.UNBAN,
    };
    ModerationService.moderateUser(postData);
  };

  /**
   * Method for remove user moderations list
   */

  deleteModerationListItem = (recipient) => {
    const data = {
      recipient,
      roomname: UserStore.user.username,
      username: UserStore.user.username,
      accessHash: UserStore.user.accessHash,
    };
    switch (this.drawerName) {
      case MODERATION_DRAWER_NAMES.MODERATORS:
        return this.removeModerator(data);
      case MODERATION_DRAWER_NAMES.SILENCES:
        return this.removeSilence(data);
      case MODERATION_DRAWER_NAMES.BANS:
      default:
        return this.removeBan(data);
    }
  };

  /**
   * @method getter searchList - search list for custom dropdown/search
   * @returns array
   */
  @computed get searchList() {
    return FirebaseStore.sortedUsers.reduce((filtered, option) => {
      if (
        UserStore.user.username !== option.originalUsername &&
        option.accessLevel !== ACCESS_LEVEL_STR.GUEST &&
        option.originalUsername.toLowerCase().includes(this.searchValue.toLocaleLowerCase())
      ) {
        const newValue = { name: option.originalUsername };
        filtered.push(newValue);
      }

      return filtered;
    }, []);
  }

  /**
   * @method getter isDrawerOpen - returning is moderation drawer open
   * @returns bool
   */
  @computed get isDrawerOpen() {
    return this.drawerName !== '';
  }

  /**
   * @method getter activeModerationList - returning list for moderation drawer
   * @returns array
   */
  @computed get activeModerationList() {
    switch (this.drawerName) {
      case MODERATION_DRAWER_NAMES.MODERATORS:
        return this.moderatorsList;
      case MODERATION_DRAWER_NAMES.SILENCES:
        return this.silencedList;
      case MODERATION_DRAWER_NAMES.BANS:
        return this.bansList;
      default:
        return [];
    }
  }

  /**
   * @method getter activeModerationTitle - returning title for moderation drawer
   * @returns string
   */
  @computed get activeModerationTitle() {
    switch (this.drawerName) {
      case MODERATION_DRAWER_NAMES.MODERATORS:
        return 'v2broadcast.manage.moderators';
      case MODERATION_DRAWER_NAMES.SILENCES:
        return 'v2broadcast.manage.silences';
      case MODERATION_DRAWER_NAMES.BANS:
      default:
        return 'v2broadcast.manage.bans';
    }
  }

  /**
   * @method getter activeModerationDescription - returning description for moderation drawer
   * @returns string
   */
  @computed get activeModerationDescription() {
    switch (this.drawerName) {
      case MODERATION_DRAWER_NAMES.MODERATORS:
        return 'v2broadcast.following.users.moderate.room';
      case MODERATION_DRAWER_NAMES.SILENCES:
        return 'v2broadcast.following.users.silenced.room';
      case MODERATION_DRAWER_NAMES.BANS:
      default:
        return 'v2broadcast.following.users.banned.room';
    }
  }
}

export default new ModerationStore(MessagingService);
